import styled, { css } from "styled-components";
import { getNewLookChipColors } from "common/Filters/common/styles.ts";

// A chevron drawn with a mask, so it takes the chip's text color in both themes.
const CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`;

// lucide "sliders-horizontal", for the shared filters drawer button.
const SLIDERS = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10 5H3'/%3E%3Cpath d='M12 19H3'/%3E%3Cpath d='M14 3v4'/%3E%3Cpath d='M16 17v4'/%3E%3Cpath d='M21 12h-9'/%3E%3Cpath d='M21 19h-5'/%3E%3Cpath d='M21 5h-7'/%3E%3Cpath d='M8 10v4'/%3E%3Cpath d='M8 12H3'/%3E%3C/svg%3E")`;

const filterPillShape = css`
  height: 32px !important;
  border-radius: ${(p) => p.theme.radii.control}px !important;
  box-shadow: none !important;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  transition: border-color 150ms, background-color 150ms;
  @media (hover: hover) {
    &:not(:where([data-chip-active])):hover {
      border-color: ${(p) => p.theme.colors.lightBorderGrey} !important;
      background: ${(p) => p.theme.colors.cardLightBgColor} !important;
    }
  }
`;

const filterPillColors = css`
  border: ${(p) => getNewLookChipColors(p.theme).border} !important;
  background: ${(p) => getNewLookChipColors(p.theme).background} !important;
  color: ${(p) => p.theme.colors.titleColor} !important;
`;

export const newLookFilterPill = css`
  ${filterPillShape};
  ${filterPillColors};
`;

/**
 * Restyles the shared single-row filter chips (selects, read-only inputs, the
 * drawer button) into the new pill family. Scoped to the marketplace.
 */
export const NewLookChipsRow = styled.div`
  min-width: 0;
  .ant-select,
  .ant-input-affix-wrapper {
    ${filterPillShape};
    position: relative;
    width: max-content !important;
    min-width: 0 !important;
    max-width: 240px !important;
    padding: 0 32px 0 14px !important;
    cursor: pointer;
  }
  .ant-input-affix-wrapper {
    ${filterPillColors};
  }
  /* A chevron on every picker chip, like the sort pill's. */
  .ant-select::after,
  .ant-input-affix-wrapper::after {
    content: "";
    position: absolute;
    top: 50%;
    right: 12px;
    width: 14px;
    height: 14px;
    margin-top: -7px;
    background-color: ${(p) => p.theme.colors.secondaryText};
    -webkit-mask: ${CHEVRON} center / 14px no-repeat;
    mask: ${CHEVRON} center / 14px no-repeat;
    pointer-events: none;
  }
  /* Phones render the read-only chip inputs bare (no clear button). */
  &&&& input.ant-input {
    ${newLookFilterPill};
    width: auto !important;
    padding: 0 32px 0 14px !important;
    text-align: left !important;
    field-sizing: content;
    min-width: 90px;
    &::placeholder {
      color: ${(p) => p.theme.colors.titleColor} !important;
      text-align: left !important;
      opacity: 1;
    }
    &:not(:placeholder-shown) {
      border-color: transparent !important;
      background: ${(p) => p.theme.colors.lightBlue} !important;
      color: ${(p) => p.theme.colors.primaryBlue} !important;
    }
  }
  div:has(> input.ant-input) {
    position: relative;
    flex: none;
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      right: 12px;
      width: 14px;
      height: 14px;
      margin-top: -7px;
      background-color: ${(p) => p.theme.colors.secondaryText};
      -webkit-mask: ${CHEVRON} center / 14px no-repeat;
      mask: ${CHEVRON} center / 14px no-repeat;
      pointer-events: none;
    }
  }
  /* Extra specificity beats the shared chip input's own background. */
  &&&&& .ant-input-affix-wrapper input.ant-input {
    height: auto !important;
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    color: inherit !important;
    font-size: 13px;
    text-align: left !important;
    cursor: pointer;
    field-sizing: content;
    min-width: 40px;
    max-width: 180px;
    &::placeholder {
      color: ${(p) => p.theme.colors.titleColor} !important;
      text-align: left !important;
      opacity: 1;
    }
  }
  /* A chip with a value reads as active and shows its clear button. */
  .ant-input-affix-wrapper:has(input:not(:placeholder-shown)) {
    border-color: transparent !important;
    background: ${(p) => p.theme.colors.lightBlue} !important;
    color: ${(p) => p.theme.colors.primaryBlue} !important;
    padding-right: 10px !important;
    &::after {
      display: none;
    }
    .ant-input-suffix {
      display: inline-flex;
      margin-left: 6px;
    }
    .ant-input-clear-icon {
      color: ${(p) => p.theme.colors.primaryBlue} !important;
      background: transparent !important;
    }
  }
  .ant-select {
    align-items: center;
  }
  .ant-select .ant-select-content,
  .ant-select .ant-select-content-item-prefix {
    display: flex;
    align-items: center;
    align-self: center;
    flex: none;
    width: auto;
    padding: 0 !important;
    font-size: 13px;
  }
  .ant-select .ant-select-content-item-suffix {
    width: 0;
    overflow: hidden;
  }
  .ant-select .ant-select-placeholder {
    display: flex;
    align-items: center;
    line-height: 20px;
    position: static !important;
    width: auto !important;
    transform: none !important;
    overflow: visible;
  }
  /* The shared drawer button (phones): same pill, lucide sliders icon. */
  div:has(> .anticon-sliders) {
    ${newLookFilterPill};
    justify-content: center;
    width: 40px;
    padding: 0 !important;
    color: ${(p) => p.theme.colors.secondaryText} !important;
    .anticon-sliders svg {
      display: none;
    }
    .anticon-sliders::before {
      content: "";
      display: block;
      width: 16px;
      height: 16px;
      background-color: currentColor;
      -webkit-mask: ${SLIDERS} center / 16px no-repeat;
      mask: ${SLIDERS} center / 16px no-repeat;
    }
  }
  .ant-select .ant-select-placeholder,
  .ant-select .ant-select-placeholder p,
  .ant-select .ant-select-selection-item {
    font-size: 13px;
  }
  .ant-select .ant-select-selection-item {
    background: transparent !important;
    border: none !important;
  }
`;
